import React from 'react';
import type { DeliveryGroupStopDraft } from './deliveryGroupTypes';
import type { ExecutionStopOption } from './executionDraftTypes';
import {
  formatExecutionUnloadingLabel,
  type ExecutionLabelContext
} from './executionTopologyHelpers';

export function formatPointTypeRu(pointType?: string | null): string {
  const pt = String(pointType || '').toLowerCase();
  if (pt === 'warehouse') return 'Склад';
  if (pt === 'consignee') return 'Получатель';
  if (pt === 'application') return 'Заявка';
  return 'Точка выгрузки';
}

export function resolvePointNameLine(
  stop: Pick<DeliveryGroupStopDraft, 'pointType' | 'pointId' | 'contactPerson'>,
  ctx?: ExecutionLabelContext,
  applicationNumber?: string | null
): string | undefined {
  const pt = String(stop.pointType || '').toLowerCase();
  if (pt === 'warehouse' && stop.pointId != null) {
    return ctx?.resolveWarehouseName?.(Number(stop.pointId))?.trim();
  }
  if (pt === 'consignee' && stop.pointId != null) {
    return ctx?.resolveConsigneeName?.(Number(stop.pointId))?.trim();
  }
  if (pt === 'application') {
    if (applicationNumber) return `№${applicationNumber}`;
    if (stop.pointId != null) return `№${stop.pointId}`;
  }
  if (stop.contactPerson?.trim()) return stop.contactPerson.trim();
  return undefined;
}

export function buildLocationLine(
  stop: Pick<DeliveryGroupStopDraft, 'city' | 'address'>
): string {
  const city = stop.city?.trim() || '';
  const address = stop.address?.trim() || '';
  return [city, address].filter(Boolean).join(', ');
}

export function enrichUnloadingStopOption(params: {
  stopUid: string;
  groupUid: string;
  position: number;
  stop: Pick<
    DeliveryGroupStopDraft,
    'pointType' | 'pointId' | 'address' | 'city' | 'contactPerson'
  >;
  labelContext?: ExecutionLabelContext;
  applicationNumber?: string | null;
}): ExecutionStopOption {
  const primaryLine = formatPointTypeRu(params.stop.pointType);
  const nameLine = resolvePointNameLine(
    params.stop,
    params.labelContext,
    params.applicationNumber
  );
  const locationLine = buildLocationLine(params.stop);
  const secondaryLine =
    [nameLine, locationLine].filter(Boolean).join(' — ') || locationLine || nameLine || '—';
  const flatLabel = formatExecutionUnloadingLabel(params.stop, params.labelContext);

  return {
    stopUid: params.stopUid,
    groupUid: params.groupUid,
    position: params.position,
    label: flatLabel,
    pointType: params.stop.pointType,
    pointId: params.stop.pointId,
    primaryLine,
    secondaryLine
  };
}

export function renderUnloadOptionLabel(option: ExecutionStopOption): React.ReactNode {
  if (!option.primaryLine) return option.label;
  return (
    <div style={{ lineHeight: 1.35, padding: '2px 0' }}>
      <div style={{ fontWeight: 600, fontSize: 12 }}>{option.primaryLine}</div>
      <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.65)' }}>
        {option.secondaryLine ?? option.label}
      </div>
    </div>
  );
}
